/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ChangeEvent, FocusEvent } from 'react';
import { useEffect } from 'react';
import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import U2000InputText from '../../../../u2000Core/components/formControls/input/U2000InputText';
import U2000ComboBox from '../../../../u2000Core/components/formControls/combobox/U2000ComboBox';
import U2000Label from '../../../../u2000Core/components/formControls/label/U2000Label';
import U2000InvalidFeedback from '../../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback';
import { NS } from '../../../U3203';
import { NS as U3202_NS } from '../../../../u3202AiProvider/U3202';
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import type { U3203AiMissionFormRow } from '../../../U3203Service';
import { useU3203Context } from '../../../U3203Context';
import { useU3203OrgProviderModelHandlers } from '../../../hooks/useU3203OrgProviderModelHandlers';
import { useU3203CorpusHandlers, type U3203CorpusOption } from '../../../hooks/useU3203CorpusHandlers';
import { useU3203MissionModelContext } from '../../../hooks/useU3203MissionModelContext';
import { u3203CategoryComboItems } from '../../../utils/u3203CategoryLabels';
import type { U3203OrgModelOption, U3203OrgProviderOption } from '../../../hooks/useU3203OrgProviderModelHandlers';
import U3202CapabilitiesBadges from '../../../../u3202AiProvider/components/common/U3202CapabilitiesBadges';
import { useU2000AuthContext } from '../../../../u2000Core/context/auth/U2000UseAuthContext';
import { U3203ROLES } from '../../../U3203';
import {
  u2000CanEditSystemMissionOrgConfig,
  u2000CanManageScopeAwareEntity,
  u2000SessionHasOrganization,
} from '../../../../u2000Core/tools/U2000SystemScopeAccess';

interface U3203AiMissionHeaderProps {
  formStateHandlers: U2000FormStateHandlers<U3203AiMissionFormRow>;
  t: TFunction;
}

function U3203AiMissionHeader({ formStateHandlers, t }: U3203AiMissionHeaderProps) {
  const { initLU } = useU3203Context();
  const { auth, hasRole } = useU2000AuthContext();
  const data = formStateHandlers.getData();
  const formEditMode = formStateHandlers.getFormEditMode();
  const isSystemMission = data?.systemScope === true;
  const formReadonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const canEditMissionHeader = u2000CanManageScopeAwareEntity({
    isSystemScope: isSystemMission,
    hasRequiredRole: hasRole(formEditMode === U2000FormStateEnumEditMode.ADD ? U3203ROLES.ADD : U3203ROLES.EDIT),
    sessionOrganizationId: auth.user.organizationId,
  });
  const canEditOrgConfig = u2000CanEditSystemMissionOrgConfig({
    isSystemScope: isSystemMission,
    hasEditRole: hasRole(U3203ROLES.EDIT),
    sessionOrganizationId: auth.user.organizationId,
  });
  const canEditOrgMission =
    !isSystemMission && hasRole(formEditMode === U2000FormStateEnumEditMode.ADD ? U3203ROLES.ADD : U3203ROLES.EDIT);

  const headerReadonly = formReadonly || (isSystemMission ? !canEditMissionHeader : !canEditOrgMission);
  const llmFieldsReadonly = isSystemMission ? formReadonly || !canEditOrgConfig : headerReadonly;

  const isCreate = formEditMode === U2000FormStateEnumEditMode.ADD;
  const providerModelRequired = !isSystemMission;
  const hideProviderModel = isSystemMission && !u2000SessionHasOrganization(auth.user.organizationId);

  const { providers, loading: providersLoading, loadProviders, loadModelsForProvider, getModelsForProvider, hasModelsLoadedForProvider } =
    useU3203OrgProviderModelHandlers();
  const { corpora, canReadCorpus, loadCorpora } = useU3203CorpusHandlers();
  const providerId = data?.defaultProviderId ?? null;
  const modelId = data?.defaultModelId ?? null;
  const { context: modelContext, loading: modelLoading } = useU3203MissionModelContext(providerId, modelId);

  useEffect(() => {
    if (hideProviderModel) return;
    void loadProviders();
  }, [hideProviderModel, loadProviders]);

  useEffect(() => {
    void loadCorpora();
  }, [loadCorpora]);

  useEffect(() => {
    if (data?.defaultProviderId) {
      void loadModelsForProvider(data.defaultProviderId);
    }
  }, [data?.defaultProviderId, loadModelsForProvider]);

  const categoryItems = u3203CategoryComboItems(t, initLU.categories).map((item) => ({
    id: item.id,
    label: item.label,
  }));

  const providerModels = getModelsForProvider(data?.defaultProviderId ?? null);

  useEffect(() => {
    if (!data?.defaultProviderId || !data?.defaultModelId) return;
    if (!hasModelsLoadedForProvider(data.defaultProviderId)) return;
    const stillValid = providerModels.some((m) => m.id === data.defaultModelId);
    if (!stillValid) {
      const evModel = { target: { id: 'defaultModelId' } } as ChangeEvent<HTMLInputElement>;
      formStateHandlers.onChange?.(evModel, null);
    }
  }, [data?.defaultProviderId, data?.defaultModelId, providerModels, formStateHandlers, hasModelsLoadedForProvider]);

  const activeChecked = data?.active === true;
  const errActive = formStateHandlers.getErrors().active;
  const hasActiveError =
    (formStateHandlers.getTouched().active && errActive !== undefined) ||
    (formStateHandlers.isSubmitted() && errActive !== undefined);

  const setActiveFromCheckbox = (checked: boolean) => {
    const ev = { target: { id: 'active' } } as ChangeEvent<HTMLInputElement>;
    formStateHandlers.onChange?.(ev, checked);
  };

  const blurActive = () => {
    formStateHandlers.onBlur?.({ target: { id: 'active' } } as FocusEvent<HTMLInputElement>);
  };

  if (!data) return null;

  return (
    <div className="space-y-4 border-b border-gray-200 pb-4">
      {data.systemScope && (
        <div className="flex items-center gap-2">
          <span className="rounded-sm bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-800">
            {t('valSystemScope', { ns: NS })}
          </span>
          <span className="text-sm text-gray-600">{t('lblSystemMissionBadge', { ns: NS })}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1.5">
          <U2000Label htmlFor="code" label={t('lblCode', { ns: NS })} required={isCreate} />
          <U2000InputText
            hideLabel
            formStateHandlers={formStateHandlers as any}
            fieldName="code"
            label={t('lblCode', { ns: NS })}
            required={isCreate}
            readonly={headerReadonly || !isCreate}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <U2000Label htmlFor="title" label={t('lblTitle', { ns: NS })} required />
          <U2000InputText
            hideLabel
            formStateHandlers={formStateHandlers as any}
            fieldName="title"
            label={t('lblTitle', { ns: NS })}
            required
            readonly={headerReadonly}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <U2000ComboBox<{ id: string; label: string }>
          formStateHandlers={formStateHandlers as any}
          fieldName="category"
          label={t('lblCategory', { ns: NS })}
          data={categoryItems}
          keyField="id"
          descField="label"
          readonly={headerReadonly}
          required
          getValue={(item) => item.id}
          findItemByValue={(value, items) => items.find((i) => i.id === value) ?? null}
          formatLabel={(item) => item.label}
        />
        <U2000InputText
          formStateHandlers={formStateHandlers as any}
          fieldName="linkedLogicalUnit"
          label={t('lblLinkedLogicalUnit', { ns: NS })}
          readonly={headerReadonly}
        />
        <div className="flex min-w-0 flex-col gap-1.5">
          <U2000Label htmlFor="active" label={t('lblActive', { ns: NS })} required />
          <div className="flex items-center pt-1">
            <div className="relative flex items-center gap-3">
              <input
                id="active"
                type="checkbox"
                checked={activeChecked}
                disabled={headerReadonly}
                onChange={(e) => {
                  if (headerReadonly) return;
                  setActiveFromCheckbox(e.target.checked);
                }}
                onBlur={blurActive}
                className="peer absolute left-0 top-1/2 h-5 w-5 -translate-y-1/2 cursor-pointer opacity-0"
                aria-checked={activeChecked}
                aria-invalid={hasActiveError}
              />
              <label
                htmlFor="active"
                className={`relative flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded border transition-all duration-200
                  ${
                    hasActiveError
                      ? 'border-red-500 bg-red-50 hover:border-red-600'
                      : headerReadonly
                        ? 'cursor-not-allowed border-gray-200 bg-muted'
                        : activeChecked
                          ? 'border-blue-500 bg-blue-500 hover:border-blue-600 hover:bg-blue-600'
                          : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-muted'
                  }
                  peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/20 peer-focus-visible:ring-offset-2
                `}
              >
                {activeChecked && (
                  <svg
                    className={headerReadonly ? 'text-gray-400' : 'text-white'}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    aria-hidden={true}
                  >
                    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </label>
              <label
                htmlFor="active"
                className={`cursor-pointer text-sm font-medium leading-relaxed whitespace-nowrap ${headerReadonly ? 'cursor-not-allowed text-gray-500' : 'text-gray-900 hover:text-gray-700'}`}
              >
                {t('lblActiveYes', { ns: NS })}
              </label>
            </div>
          </div>
          <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName="active" />
        </div>
      </div>

      {!hideProviderModel ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <U2000ComboBox<U3203OrgProviderOption>
            formStateHandlers={formStateHandlers as any}
            fieldName="defaultProviderId"
            label={t('lblDefaultProvider', { ns: NS })}
            data={providers}
            keyField="id"
            descField="name"
            readonly={llmFieldsReadonly}
            required={providerModelRequired}
            getValue={(item) => item.id}
            findItemByValue={(value, items) => items.find((i) => i.id === value) ?? null}
            formatLabel={(item) => `${item.name} (${item.code})`}
          />
          <U2000ComboBox<U3203OrgModelOption>
            formStateHandlers={formStateHandlers as any}
            fieldName="defaultModelId"
            label={t('lblDefaultModel', { ns: NS })}
            data={providerModels}
            keyField="id"
            descField="name"
            readonly={llmFieldsReadonly || !data?.defaultProviderId}
            disabled={!data?.defaultProviderId}
            required={providerModelRequired}
            getValue={(item) => item.id}
            findItemByValue={(value, items) => items.find((i) => i.id === value) ?? null}
            formatLabel={(item) => `${item.name} (${item.code})`}
          />
        </div>
      ) : null}

      {!hideProviderModel && canEditOrgConfig && !providersLoading && providers.length === 0 ? (
        <p className="text-sm text-amber-700">
          {t('orgConfig.msgNoProviders', { ns: NS })}{' '}
          <Link to="/U3202" className="font-medium text-blue-600 underline hover:text-blue-800">
            {t('orgConfig.lnkU3202', { ns: NS })}
          </Link>
        </p>
      ) : null}

      {!hideProviderModel && canEditOrgConfig ? (
        <p className="text-sm text-gray-600">{t('orgConfig.steps', { ns: NS })}</p>
      ) : null}

      <div className="flex min-w-0 flex-col gap-1.5">
        <U2000ComboBox<U3203CorpusOption>
          formStateHandlers={formStateHandlers as any}
          fieldName="corpusId"
          label={t('lblCorpusRag', { ns: NS })}
          data={corpora}
          keyField="id"
          descField="title"
          readonly={headerReadonly || !canReadCorpus}
          disabled={!canReadCorpus}
          noneItemLabel={t('optCorpusRagNone', { ns: NS })}
          getValue={(item) => item.id}
          findItemByValue={(value, items) => items.find((i) => i.id === value) ?? null}
          formatLabel={(item) => `${item.title} (${item.code})`}
        />
        {!canReadCorpus ? (
          <p className="text-sm text-amber-700">{t('msgCorpusNoAccess', { ns: NS })}</p>
        ) : (
          <p className="text-sm text-gray-600">
            {t('helpCorpusRag', { ns: NS })}{' '}
            <Link to="/U3208" className="font-medium text-blue-600 underline hover:text-blue-800">
              {t('lnkOpenCorpusU3208', { ns: NS })}
            </Link>
          </p>
        )}
      </div>

      {!hideProviderModel && modelId && providerId ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-800">{t('lblCapabilities', { ns: NS })}</h3>
          {modelLoading && !modelContext ? (
            <p className="text-sm text-gray-600">{t('msgLoading', { ns: NS })}</p>
          ) : (
            <U3202CapabilitiesBadges capabilities={modelContext?.capabilities} t={t} ns={U3202_NS} />
          )}
        </div>
      ) : null}
    </div>
  );
}

export default U3203AiMissionHeader;
